import { Field as BaseField } from "@base-ui/react/field";
import clsx from "clsx";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon";
import styles from "./Field.module.css";

/** Props every labelled control shares. A visible label is required; hideLabel keeps it for screen readers. */
export interface FieldLabelProps {
  label: ReactNode;
  /** Visually hide the label (inline table inputs); it stays the accessible name. */
  hideLabel?: boolean;
  /** Helper text under the control, wired to aria-describedby. */
  description?: ReactNode;
  /** An error message in the user's terms ("Mass must be more than 0 g"); marks the field invalid. */
  error?: string | null;
  /** Marks the minority case when most fields in a form are required. */
  optional?: boolean;
}

export function FieldLabel({ label, hideLabel, optional }: Pick<FieldLabelProps, "label" | "hideLabel" | "optional">) {
  return (
    <BaseField.Label className={hideLabel ? styles.hiddenLabel : styles.label}>
      {label}
      {optional && !hideLabel ? <span className={styles.optional}> (optional)</span> : null}
    </BaseField.Label>
  );
}

export function FieldFooter({ description, error }: Pick<FieldLabelProps, "description" | "error">) {
  return (
    <>
      {description ? <BaseField.Description className={styles.description}>{description}</BaseField.Description> : null}
      {error ? (
        <BaseField.Error className={styles.error} match>
          <Icon name="alertCircle" />
          <span>{error}</span>
        </BaseField.Error>
      ) : (
        <BaseField.Error className={styles.error} />
      )}
    </>
  );
}

export interface FormFieldProps extends FieldLabelProps {
  name?: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Label, description and error around a custom control (a control built from other components).
 * Standard controls (TextField, NumberField, Select…) already include this.
 */
export function FormField({
  label,
  hideLabel,
  description,
  error,
  optional,
  name,
  disabled,
  className,
  children,
}: FormFieldProps) {
  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(styles.field, className)}
    >
      <FieldLabel label={label} hideLabel={hideLabel} optional={optional} />
      {children}
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
